import type { Device } from '@glyph/protocol-ts';
import {
  Button,
  Card,
  CardHeader,
  MenuRow,
  Section,
  SegmentedControl,
  StatusPill,
  useClock,
} from '@glyph/ui';

export interface MenuEntry {
  key: string;
  label: string;
  meta?: string;
  onSelect: () => void;
}
export interface MenuScreenProps {
  devices?: Device[];
  currentDeviceId?: string;
  onPickDevice?: (id: string) => void;
  deviceTitle: string;
  deviceEntries: MenuEntry[];
  fleetEntries?: MenuEntry[];
  theme: 'dark' | 'light';
  onTheme: (t: 'dark' | 'light') => void;
  user?: { name: string; role: string };
  relayOk?: boolean;
  onSignOut?: () => void;
  signOutLabel?: string;
  /** Console build id (cloud): shown under the account so a stale home-screen app is easy to spot. */
  build?: string;
}

/** Mobile "More" menu: device switcher, device pages, fleet pages, theme, account. */
export function MenuScreen({
  devices,
  currentDeviceId,
  onPickDevice,
  deviceTitle,
  deviceEntries,
  fleetEntries,
  theme,
  onTheme,
  user,
  relayOk,
  onSignOut,
  signOutLabel = 'SIGN OUT',
  build,
}: MenuScreenProps) {
  const clock = useClock();
  return (
    <Section>
      {devices && devices.length > 0 && (
        <Card>
          <CardHeader title="Device" meta="TAP TO SWITCH" />
          {devices.map((d) => (
            <button
              key={d.id}
              type="button"
              onClick={() => onPickDevice?.(d.id)}
              className={`flex w-full items-center justify-between gap-3 border-0 border-t border-line px-4 py-3 text-left text-fg ${d.id === currentDeviceId ? 'bg-selected' : 'bg-transparent'}`}
            >
              <div className="flex min-w-0 flex-col gap-[5px]">
                <span className="text-xl font-medium">{d.name}</span>
                <span className="font-mono text-sm text-fg4">{d.assignment?.event_name ?? 'Unassigned'}</span>
              </div>
              <StatusPill state={d.state}>{d.state}</StatusPill>
            </button>
          ))}
        </Card>
      )}
      <Card>
        <CardHeader title={deviceTitle} />
        {deviceEntries.map((e) => (
          <MenuRow key={e.key} label={e.label} meta={e.meta} onPress={e.onSelect} />
        ))}
      </Card>
      {fleetEntries && fleetEntries.length > 0 && (
        <Card>
          <CardHeader title="Fleet" />
          {fleetEntries.map((e) => (
            <MenuRow key={e.key} label={e.label} meta={e.meta} onPress={e.onSelect} />
          ))}
        </Card>
      )}
      <Card>
        <CardHeader title="Appearance" />
        <div className="flex flex-col gap-2.5 border-t border-line px-4 pb-3.5 pt-3">
          <span className="text-lg text-fg">Theme</span>
          <SegmentedControl
            fullWidth
            size="mobile"
            value={theme}
            onChange={onTheme}
            options={[
              { value: 'dark', label: 'Dark' },
              { value: 'light', label: 'Light' },
            ]}
          />
        </div>
      </Card>
      {user && (
        <Card>
          <div className="flex min-h-12 items-center justify-between gap-3 px-4 py-2.5">
            <div className="flex flex-col gap-1">
              <span className="text-lg text-fg">{user.name}</span>
              <span className="font-mono text-sm text-fg4">
                {user.role.toUpperCase()} ·{' '}
                {relayOk === undefined ? '' : relayOk ? 'RELAY OK · ' : 'RELAY DOWN · '}
                {clock} UTC
              </span>
              {build && <span className="font-mono text-xs text-fg5">console {build}</span>}
            </div>
            {onSignOut && (
              <Button size="mobile" variant="ghost" mono onClick={onSignOut}>
                {signOutLabel}
              </Button>
            )}
          </div>
        </Card>
      )}
    </Section>
  );
}
